import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faTimes, 
  faCamera, 
  faCheckCircle, 
  faTimesCircle 
} from '@fortawesome/free-solid-svg-icons';
import styles from './QRScanner.module.css';

const QrScanner = ({ 
  isOpen, 
  onClose, 
  onScan, 
  mode, 
  checklistMode, 
  isDevMode = process.env.NODE_ENV === 'development'
}) => {
  const scannerRef = useRef(null);
  const readerContainerRef = useRef(null);
  const [scannerError, setScannerError] = useState('');
  const [scanSuccessMessage, setScanSuccessMessage] = useState('');
  const [processingQr, setProcessingQr] = useState(false);

  // Handle successful scan
  const handleSuccessfulScan = (decodedText) => {
    console.log('QR Scan data:', decodedText);
    setProcessingQr(true);
    
    try {
      // Pass the scan data to the parent component
      onScan(decodedText, setScanSuccessMessage, setScannerError);
    } catch (error) {
      console.error('Error handling QR code:', error);
      setScannerError(`Error processing QR code: ${error.message}`);
    } finally {
      setProcessingQr(false);
    }
  };

  // Initialize and cleanup QR scanner
  useEffect(() => {
    if (!isOpen) return;

    // Dynamic import to avoid SSR issues
    const loadScanner = async () => {
      try {
        const { Html5QrcodeScanner, Html5QrcodeScanType } = await import('html5-qrcode');
    
        if (readerContainerRef.current && !scannerRef.current) {
          scannerRef.current = new Html5QrcodeScanner(
            'qr-reader',
            { 
              fps: 10,
              qrbox: 250,
              aspectRatio: 1.0,
              supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA]
            },
            false
          );
    
          scannerRef.current.render(
            handleSuccessfulScan, 
            (errorMessage) => {
              console.error('QR scan error:', errorMessage);
              if (errorMessage.includes('permission')) {
                setScannerError('Error accessing camera. Please check camera permissions and try again.');
              }
            }
          );
        }
      } catch (error) {
        console.error('Error loading QR scanner:', error);
        setScannerError('Failed to load QR scanner. Please try again.');
      }
    };
    
    loadScanner();
    
    // Cleanup function
    return () => {
      if (scannerRef.current) {
        try {
          scannerRef.current.clear();
          scannerRef.current = null;
        } catch (error) {
          console.error('Error clearing scanner:', error);
        }
      }
    };
  }, [isOpen, onScan]);

  if (!isOpen) return null;

  return (
    <div className={styles.scannerModal}>
      <div className={styles.scannerContent}>
        <div className={styles.scannerHeader}>
          <h2>{checklistMode === 'checkIn' ? 'Check-In' : 'Check-Out'} Scanner</h2>
          <button 
            className={styles.closeButton}
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className={styles.scannerModeSelector}>
          <label className={styles.scannerModeLabel}>
            <input
              type="radio"
              name="scannerMode"
              value="individual"
              checked={mode === 'individual'}
              onChange={() => onScan('CHANGE_MODE_INDIVIDUAL')}
            />
            <span>Individual Mode (Mark one item)</span>
          </label>
          
          <label className={styles.scannerModeLabel}>
            <input
              type="radio"
              name="scannerMode"
              value="markAll"
              checked={mode === 'markAll'}
              onChange={() => onScan('CHANGE_MODE_MARKALL')}
            />
            <span>Mark All Mode (Mark all items in group)</span>
          </label>
        </div>
        
        <div className={styles.scannerArea}>
          {processingQr ? (
            <div className={styles.scannerProcessing}>
              <FontAwesomeIcon icon={faSpinner} spin />
              <p>Processing QR code...</p>
            </div>
          ) : (
            <>
              <div id="qr-reader" ref={readerContainerRef} className={styles.cameraContainer}></div>
              
              <div className={styles.scanInstructions}>
                <FontAwesomeIcon icon={faCamera} />
                <p>Point camera at QR code on equipment</p>
              </div>
            </>
          )}
          
          {scannerError && (
            <div className={styles.scannerError}>
              <FontAwesomeIcon icon={faTimesCircle} />
              <p>{scannerError}</p>
            </div>
          )}
          
          {scanSuccessMessage && (
            <div className={styles.scannerSuccess}>
              <FontAwesomeIcon icon={faCheckCircle} />
              <p>{scanSuccessMessage}</p>
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default QrScanner;